import { css } from 'aphrodite';
import { toast } from 'sonner';
import { Waypoint } from 'react-waypoint';
import { useContactSuggestedTagsQuery } from '../../../../../queries';
import { canUserModifyTag } from '../../../../../models/utils/tagUtils';
import { IAccountTag, IImpersonationContext } from '../../../../../viewmodels/AppViewModels';
import { brandPrimary, green4, surveysTint } from '../../../../styles/colors';
import { baseStyleSheet } from '../../../../styles/styles';
import { LoadingSpinner } from '../../../LoadingSpinner';
import { TinyPopover } from '../../../TinyPopover';
import { HistoryIcon } from '../../../svgs/icons/HistoryIcon';
import { StarIcon } from '../../../svgs/icons/StarIcon';
import { TagIcon } from '../../../svgs/icons/TagIcon';
import { Tag } from '../../../tags/Tag';
import { useAllTagsList } from '../hooks/useAllTagsList';
import { useTagsStorage } from './hooks';
import { styleSheet } from './styles';
import './styles.less';

const LOCKED_TAG_MESSAGE = 'Only admins can apply this tag.';

export const AddTagInput = ({
	isAutocompleteOpen,
	highlightedAutoCompleteSuggestionIndex,
	pendingTag,
	input,
	isBusy,
	suggestions,
	searchResults,
	recentSearches,
	addTag,
	isAdmin,
	existingTagNames,
}: {
	input: JSX.Element;
	pendingTag: string | undefined;
	highlightedAutoCompleteSuggestionIndex?: number;
	isAutocompleteOpen: boolean | undefined;
	isBusy: boolean;
	suggestions: (string | IAccountTag)[];
	searchResults: (string | IAccountTag)[];
	recentSearches: (string | IAccountTag)[];
	addTag: (pendingTag: IAccountTag) => void;
	isAdmin: boolean;
	existingTagNames?: string[];
}) => {
	const isBrowsing = !pendingTag?.length;
	const isOpen = (isBrowsing || isBusy || suggestions.length > 0 || searchResults?.length === 0) && isAutocompleteOpen;

	return (
		<TinyPopover
			anchor={input}
			contentStyles={[styleSheet.autoCompleteMenu, styleSheet.autoCompleteMenuInnerStyles]}
			isOpen={isOpen}
		>
			<div>
				{isBusy ? (
					<div className={css(styleSheet.autoCompleteMenuItem, styleSheet.autoCompleteMenuCenterAlign)}>
						<LoadingSpinner type='small' />
					</div>
				) : !isBrowsing || suggestions.length > 0 ? (
					<TagsList
						title={!pendingTag?.length ? 'Favorites' : 'Top Results'}
						icon={
							!pendingTag?.length ? (
								<StarIcon fillColor={surveysTint} className={css(styleSheet.sectionIcon)} filled={true} />
							) : (
								<></>
							)
						}
						altKey='searches-suggestions'
						list={suggestions}
						highlightedAutoCompleteSuggestionIndex={highlightedAutoCompleteSuggestionIndex}
						addTag={addTag}
						isAdmin={isAdmin}
					/>
				) : null}
				{!pendingTag?.length && recentSearches?.length > 0 ? (
					<>
						<TagsList
							title='Recent Searches'
							icon={<HistoryIcon fillColor={green4} className={css(styleSheet.sectionIcon)} />}
							altKey='searches'
							list={recentSearches.slice(0, 3)}
							newIndexStart={suggestions.length}
							highlightedAutoCompleteSuggestionIndex={highlightedAutoCompleteSuggestionIndex}
							addTag={addTag}
							isAdmin={isAdmin}
						/>
					</>
				) : null}
				{suggestions?.length === 0 && pendingTag ? (
					<button
						type='button'
						onMouseDown={() => addTag({ name: pendingTag })}
						className={css(styleSheet.autoCompleteMenuItem, styleSheet.addTagButton)}
						key={`${pendingTag}-pending`}
						title={pendingTag}
					>
						<span className={css(styleSheet.sectionTitle)}>{`Create new tag "${pendingTag}"`}</span>
					</button>
				) : null}
				{isBrowsing && !isBusy && isOpen ? (
					<AllTagsSection addTag={addTag} isAdmin={isAdmin} existingTagNames={existingTagNames} />
				) : null}
			</div>
		</TinyPopover>
	);
};

export const AllTagsSection = ({
	addTag,
	isAdmin,
	existingTagNames = [],
}: {
	addTag: (tag: IAccountTag) => void;
	isAdmin: boolean;
	existingTagNames?: string[];
}) => {
	const excludedTagNames = new Set(existingTagNames);
	const { fetchNextPage, hasNextPage, isFetchingNextPage, status, tags } = useAllTagsList({ excludedTagNames });

	if (status === 'pending') {
		return (
			<div className={css(styleSheet.autoCompleteMenuItem, styleSheet.autoCompleteMenuCenterAlign)}>
				<LoadingSpinner type='small' />
			</div>
		);
	}
	if (status === 'error') {
		return (
			<p className={css(baseStyleSheet.m0)}>
				<span className={css(styleSheet.noResult)}>Couldn&apos;t load all tags.</span>
			</p>
		);
	}
	if (tags.length === 0 && !hasNextPage) {
		return null;
	}
	return (
		<>
			{tags.length > 0 ? (
				<TagsList
					title='All Tags'
					icon={<TagIcon fillColor={brandPrimary} className={css(styleSheet.sectionIcon)} />}
					altKey='all-tags'
					list={tags}
					addTag={addTag}
					isAdmin={isAdmin}
				/>
			) : null}
			{isFetchingNextPage ? (
				<div className={css(styleSheet.autoCompleteMenuItem, styleSheet.autoCompleteMenuCenterAlign)}>
					<LoadingSpinner type='small' />
				</div>
			) : null}
			{hasNextPage && !isFetchingNextPage ? <Waypoint bottomOffset='-100px' onEnter={() => fetchNextPage()} /> : null}
		</>
	);
};

export const FeaturedAndRecentTags = ({
	onSelected,
	showNoResults,
	impersonationContext,
}: {
	onSelected: (tag: IAccountTag) => void;
	showNoResults: boolean;
	impersonationContext?: IImpersonationContext;
}) => {
	const tagsQuery = useContactSuggestedTagsQuery({
		addKeepInTouchTag: false,
		pageSize: 25,
		impersonationContext,
	});
	const { recentSearches } = useTagsStorage();
	const suggestions = (tagsQuery.data as (string | IAccountTag)[]) || [];

	return (
		<>
			{!showNoResults ? (
				<>
					{suggestions?.length > 0 ? (
						<TagsList
							title='Favorites'
							icon={<StarIcon fillColor={surveysTint} className={css(styleSheet.sectionIcon)} filled={true} />}
							altKey='searches-suggestions'
							list={suggestions.slice(0, 3)}
							addTag={onSelected}
						/>
					) : null}
					{recentSearches?.length > 0 ? (
						<TagsList
							title='Recent Searches'
							icon={<HistoryIcon fillColor={green4} className={css(styleSheet.sectionIcon)} />}
							altKey='searches'
							list={recentSearches.slice(0, 3)}
							addTag={onSelected}
						/>
					) : null}
				</>
			) : null}
			{showNoResults ? (
				<p className={css(baseStyleSheet.m0)}>
					<span className={css(styleSheet.noResult)}>No Results</span>
				</p>
			) : null}
		</>
	);
};

export const TagsList = ({
	title,
	icon,
	list,
	highlightedAutoCompleteSuggestionIndex,
	addTag,
	altKey,
	newIndexStart = 0,
	isAdmin = false,
}: {
	title: string;
	icon: JSX.Element;
	list: (string | IAccountTag)[];
	highlightedAutoCompleteSuggestionIndex?: number;
	addTag: (pendingTag: IAccountTag) => void;
	altKey?: string;
	newIndexStart?: number;
	isAdmin?: boolean;
}) => {
	return (
		<>
			{!icon ? (
				<div className={css(styleSheet.sectionWarp)}>
					<p className={css(styleSheet.sectionTitle)}>{title}</p>
				</div>
			) : (
				<figure className={css(styleSheet.sectionWarp)}>
					{icon} <figcaption className={css(styleSheet.sectionTitle)}>{title}</figcaption>
				</figure>
			)}
			{list.map((x, i) => {
				const index = i + newIndexStart;
				const isAccountTag = typeof x !== 'string';
				const tagObject: IAccountTag = isAccountTag ? x : { name: x };
				const stringValue = tagObject.name;
				const cannotApply = isAccountTag && !canUserModifyTag(x, isAdmin);
				const className = `${css(
					styleSheet.autoCompleteMenuItem,
					highlightedAutoCompleteSuggestionIndex === index ? styleSheet.autoCompleteMenuItemHighlighted : null,
					cannotApply ? styleSheet.autoCompleteMenuItemDisabled : null
				)} truncate-text`;

				const handleSelect = () => {
					if (cannotApply) {
						toast.error(LOCKED_TAG_MESSAGE);
						return;
					}
					addTag(isAccountTag ? tagObject : { name: stringValue });
				};

				return (
					<button
						onMouseDown={handleSelect}
						className={className}
						key={`${stringValue}-${i} ${altKey ?? ''}`}
						title={cannotApply ? LOCKED_TAG_MESSAGE : stringValue}
						aria-disabled={cannotApply}
					>
						<Tag tagValue={tagObject} />
					</button>
				);
			})}
		</>
	);
};
